import React, { useState, useContext, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Send, Mic, Stop } from "@mui/icons-material";
import { ChatContext } from "../context/chatcontext";
import ModelSelector from "./modelselector";

export default function ChatInput() {
  const [input, setInput] = useState("");
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const { addMessage, isProcessing, stopGeneration } = useContext(ChatContext);

  // 🎤 Setup mic
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.lang = "en-IN";
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.onresult = (event) => {
        let transcript = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setInput(transcript);
      };
      recognition.onend = () => setListening(false);
      recognitionRef.current = recognition;
    }
  }, []);

  // 🚀 Send Message
  const handleSend = async () => {
    if (!input.trim()) return;
    
    const messageToSend = input.trim();
    setInput(""); // Clear input immediately
    
    // Use a small timeout to ensure the state update processes before any heavy work
    setTimeout(async () => {
      try {
        const userMessage = {
          role: "user",
          content: messageToSend,
        };

        // Send the message which will trigger processing animations
        await addMessage(userMessage);
      } catch (error) {
        console.error("Failed to send message:", error);
      }
    }, 0);
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 2, bgcolor: "#FFFFFF" }}>
      <Paper
        elevation={10}
        sx={{
          display: "flex",
          alignItems: "center",
          width: "75%",
          maxWidth: "950px",
          borderRadius: 4,
          px: 2,
          py: 1.2,
          bgcolor: "#F0F8FF",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ flex: 1 }}
        >
          {/* 🎤 Mic */}
          <Tooltip title={listening ? "Stop Listening" : "Speak"}>
            <IconButton
              color={listening ? "error" : "primary"}
              onClick={() => {
                if (!recognitionRef.current) return;
                if (listening) {
                  recognitionRef.current.stop();
                  setListening(false);
                } else {
                  recognitionRef.current.start();
                  setListening(true);
                }
              }}
            >
              {listening ? <Stop /> : <Mic />}
            </IconButton>
          </Tooltip>

          {/* 🤖 Model Selector near input */}
          <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
            <ModelSelector />
          </Box>

          {/* 📝 Text Field */}
          <TextField
            fullWidth
            placeholder="Type or speak your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
            InputProps={{
              sx: {
                color: "black",
                bgcolor: "#FFFFFF",
                borderRadius: 2,
                border: "1px solid #87CEEB",
                "& fieldset": { border: "none" },
              },
            }}
          />

          {/* 📤 Send / Stop */}
          {isProcessing ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={24} sx={{ color: "#87CEEB" }} />
              <Tooltip title="Stop Generation">
                <IconButton
                  onClick={stopGeneration}
                  sx={{
                    bgcolor: "#ff4444",
                    color: "white",
                    "&:hover": { bgcolor: "#cc0000" },
                    borderRadius: 2,
                    p: 0.5
                  }}
                >
                  <Stop fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            <Tooltip title="Send">
              <IconButton
                onClick={handleSend}
                disabled={isProcessing}
                sx={{
                  bgcolor: "#87CEEB",
                  color: "#FFFFFF",
                  "&:hover": { bgcolor: "#00BFFF" },
                  "&:disabled": { bgcolor: "#E0E0E0", color: "#9E9E9E" },
                  borderRadius: 2,
                }}
              >
                <Send fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
